import React, { useEffect, useState, useCallback, useMemo } from 'react';

interface SessionTimeoutModalProps {
  isVisible: boolean;
  timeRemaining: number;
  onExtendSession: () => void;
  onSessionExpired: () => void;
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = React.memo(({ 
  isVisible, 
  timeRemaining, 
  onExtendSession, 
  onSessionExpired 
}) => {
  const [countdown, setCountdown] = useState(timeRemaining);

  // Memoize the formatted time to prevent unnecessary recalculations
  const formattedTime = useMemo(() => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [countdown]);

  // Memoize the progress percentage
  const progressPercentage = useMemo(() => {
    return Math.max(0, (countdown / 60) * 100); // 60 seconds = 100%
  }, [countdown]);

  // Memoize the progress color
  const progressColor = useMemo(() => {
    if (countdown > 30) return '#10B981'; // green
    if (countdown > 15) return '#F59E0B'; // yellow
    return '#EF4444'; // red
  }, [countdown]);

  // Memoize the warning message
  const warningMessage = useMemo(() => {
    if (countdown > 30) return 'Your session will expire soon.';
    if (countdown > 15) return 'Your session is about to expire!';
    return 'Your session is expiring very soon!';
  }, [countdown]);

  // Memoize the button handlers
  const handleExtend = useCallback(() => {
    onExtendSession();
  }, [onExtendSession]);

  // Memoize the countdown effect
  useEffect(() => {
    if (!isVisible) {
      setCountdown(timeRemaining);
      return;
    }

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onSessionExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, timeRemaining, onSessionExpired]);

  // Focus trap and prevent keyboard events from closing modal
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent any keyboard shortcuts from closing the modal
      e.preventDefault();
      e.stopPropagation();
      
      // Only allow specific actions
      if (e.key === 'Enter' || e.key === ' ') {
        // Allow Enter or Space to trigger the primary action (Extend Session)
        handleExtend();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);

    // Focus the modal
    const modal = document.querySelector('.session-timeout-modal');
    if (modal) {
      (modal as HTMLElement).focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [isVisible, handleExtend]);

  // Reset countdown when timeRemaining changes
  useEffect(() => {
    setCountdown(timeRemaining);
  }, [timeRemaining]);

  const handleLogout = useCallback(() => {
    onSessionExpired();
  }, [onSessionExpired]);

  if (!isVisible) return null;

  return (
    <div 
      className="session-timeout-modal fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999]"
      onKeyDown={(e) => {
        // Prevent modal from closing on any key press, especially Command key
        e.preventDefault();
        e.stopPropagation();
      }}
      tabIndex={-1}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl border-4 border-red-500 overflow-hidden">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Session Timeout Warning
          </h3>
          
          <p className="text-gray-600 mb-4">
            {warningMessage}
          </p>
          
          <div className="mb-4 px-2">
            <div className="text-3xl font-mono font-bold text-gray-900 mb-2">
              {formattedTime}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ 
                  width: `${Math.min(progressPercentage, 100)}%`, 
                  backgroundColor: progressColor 
                }}
              />
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleExtend}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Extend Session
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

SessionTimeoutModal.displayName = 'SessionTimeoutModal';

export default SessionTimeoutModal; 